import React, { useState, useEffect } from 'react';
import { useAxios } from '../core/_axios';
import { Account } from '../core/_models';
import { EmailSettings, EmailTemplate } from '../core/_models_email';
import { getEmailSettings, updateEmailSettings, getEmailTemplates } from '../core/_requests_email';
import LoadingSpinner from './LoadingSpinner';

interface AccountEmailSettingsProps {
  account: Account;
  onUpdate?: () => void;
}

const AccountEmailSettings: React.FC<AccountEmailSettingsProps> = ({ account, onUpdate }) => {
  const axios = useAxios();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<EmailSettings | null>(null);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [account.unique_identifier]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load email settings and templates in parallel
      const [emailSettings, emailTemplates] = await Promise.all([
        getEmailSettings(axios, account.unique_identifier),
        getEmailTemplates(axios, account.unique_identifier)
      ]);
      
      setSettings(emailSettings);
      setTemplates(emailTemplates);
    } catch (err) {
      console.error('Failed to load email settings:', err);
      setError('Failed to load email settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;
    
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);
      
      const updatedSettings = await updateEmailSettings(axios, account.unique_identifier, settings);
      setSettings(updatedSettings);
      setSuccessMessage('Email settings saved successfully!');
      
      if (onUpdate) {
        onUpdate();
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to save email settings:', err);
      setError('Failed to save email settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!settings) {
    return <div className="alert alert-danger">Failed to load email settings</div>;
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Email Follow-up Settings</h3>
        <div className="card-toolbar">
          <span className="badge badge-light-info">
            Lead Scoring Enabled
          </span>
        </div>
      </div>
      
      <div className="card-body">
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {error}
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setError(null)}
              aria-label="Close"
            ></button>
          </div>
        )}
        
        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            {successMessage}
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setSuccessMessage(null)}
              aria-label="Close"
            ></button>
          </div>
        )}

        <div className="row mb-6">
          <div className="col-lg-6">
            <div className="form-check form-switch form-check-custom form-check-solid">
              <input
                className="form-check-input"
                type="checkbox"
                id="emailEnabled"
                checked={settings.enabled}
                onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
              />
              <label className="form-check-label fw-bold text-gray-800" htmlFor="emailEnabled">
                Enable Email Follow-up
              </label>
              <div className="text-muted fs-7 mt-1">
                Send personalized thank-you emails to guestbook signers
              </div>
            </div>
          </div>
        </div>

        {settings.enabled && (
          <>
            <div className="separator separator-dashed my-6"></div>

            <div className="row mb-6">
              <div className="col-lg-6">
                <div className="form-check form-switch form-check-custom form-check-solid">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="automaticSending"
                    checked={settings.automatic_sending}
                    onChange={(e) => setSettings({ ...settings, automatic_sending: e.target.checked })}
                  />
                  <label className="form-check-label fw-bold text-gray-800" htmlFor="automaticSending">
                    Automatic Sending
                  </label>
                  <div className="text-muted fs-7 mt-1">
                    Automatically send emails for high-quality leads (80+ score)
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="form-check form-switch form-check-custom form-check-solid">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="dryRunMode"
                    checked={settings.dry_run_mode}
                    onChange={(e) => setSettings({ ...settings, dry_run_mode: e.target.checked })}
                  />
                  <label className="form-check-label fw-bold text-gray-800" htmlFor="dryRunMode">
                    Dry Run Mode
                  </label>
                  <div className="text-muted fs-7 mt-1">
                    Test email generation without actually sending emails
                  </div>
                </div>
              </div>
            </div>

            <div className="separator separator-dashed my-6"></div>

            <div className="row mb-6">
              <label className="col-lg-4 col-form-label fw-bold fs-6">Email Template</label>
              <div className="col-lg-8">
                <select
                  className="form-select form-select-solid"
                  value={settings.template_id || ''}
                  onChange={(e) => setSettings({ ...settings, template_id: e.target.value || null })}
                >
                  <option value="">Default Template</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
                <div className="form-text">
                  Choose a custom template for email generation
                </div>
              </div>
            </div>

            {settings.dry_run_mode && (
              <div className="notice d-flex bg-light-warning rounded border-warning border border-dashed p-6">
                <i className="ki-duotone ki-information fs-2tx text-warning me-4">
                  <span className="path1"></span>
                  <span className="path2"></span>
                  <span className="path3"></span>
                </i>
                <div className="d-flex flex-stack flex-grow-1">
                  <div className="fw-bold">
                    <h4 className="text-gray-900 fw-bolder">Dry Run Mode Active</h4>
                    <div className="fs-6 text-gray-700">
                      Emails will be generated and queued but not actually sent. Perfect for testing!
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="card-footer d-flex justify-content-end">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving...
            </>
          ) : (
            'Save Settings'
          )}
        </button>
      </div>
    </div>
  );
};

export default AccountEmailSettings;